import { Users, Plus, X, Mail, Building2, Loader2, Calendar, Clipboard, Check, Search, Trash2, MoreHorizontal, Zap, CheckCircle2, Lock } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/useAppContext';
import { supabase } from '../lib/supabase';
import { formatDate } from '../utils/formatters';
import { getPlanLimits, formatLimit } from '../lib/plans';
import { usePresenceFor } from '../lib/presence';
import EmptyState from '../components/EmptyState';
import PresenceIndicator from '../components/PresenceIndicator';
import type { TenantLink } from '../context/types';

// ─── Portal dropdown ──────────────────────────────────────────────────────────

interface DropdownPos { top: number; left: number; openUpward: boolean; }

interface TenantMenuProps {
    pos: DropdownPos;
    onEdit: () => void;
    onDelete: () => void;
    onClose: () => void;
}

function TenantMenu({ pos, onEdit, onDelete, onClose }: TenantMenuProps) {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const handler = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) onClose();
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [onClose]);

    const MENU_WIDTH = 220;

    const style: React.CSSProperties = {
        position: 'fixed',
        left: pos.left,
        width: MENU_WIDTH,
        background: '#fff',
        boxShadow: '0 20px 40px -8px rgba(0,0,0,0.15)',
        borderRadius: '18px',
        padding: '8px',
        zIndex: 9999,
        border: '1px solid #e2e8f0',
        textAlign: 'left',
        animation: 'tenantMenuIn 0.15s ease-out',
        ...(pos.openUpward
            ? { bottom: `calc(100vh - ${pos.top}px)` }
            : { top: pos.top }),
    };

    return createPortal(
        <div ref={ref} style={style}>
            <div style={{ padding: '10px 14px', fontSize: '0.7rem', fontWeight: 800, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Management
            </div>
            <button
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', background: 'none', border: 'none',
                    borderRadius: '12px', cursor: 'pointer', fontSize: '0.9375rem',
                    fontWeight: 600, color: 'var(--color-text)', transition: 'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = 'none')}
            >
                <div style={{ background: '#f8fafc', padding: '6px', borderRadius: '8px' }}>
                    <Mail size={14} />
                </div>
                Edit Tenant
            </button>
            <div style={{ height: '1px', background: '#f1f5f9', margin: '6px 0' }} />
            <button
                onClick={(e) => { e.stopPropagation(); onDelete(); onClose(); }}
                style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: '12px',
                    padding: '12px 14px', background: 'rgba(239,68,68,0.03)', border: 'none',
                    borderRadius: '12px', cursor: 'pointer', fontSize: '0.9375rem',
                    fontWeight: 700, color: '#dc2626', transition: 'background 0.15s',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.08)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(239,68,68,0.03)')}
            >
                <div style={{ background: 'rgba(239,68,68,0.08)', padding: '6px', borderRadius: '8px' }}>
                    <Trash2 size={14} />
                </div>
                Delete Tenant
            </button>
        </div>,
        document.body,
    );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function TenantsPage() {
    const { user } = useAppContext();
    const navigate = useNavigate();
    const [tenantLinks, setTenantLinks] = useState<TenantLink[]>([]);
    const [properties, setProperties] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [showUpgradeGate, setShowUpgradeGate] = useState(false);
    const [editingTenant, setEditingTenant] = useState<TenantLink | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [copiedId, setCopiedId] = useState<string | null>(null);

    // Portal dropdown state
    const [openMenuTenantId, setOpenMenuTenantId] = useState<string | null>(null);
    const [menuPos, setMenuPos] = useState<DropdownPos | null>(null);

    // Form State
    const [tenantName, setTenantName] = useState('');
    const [tenantEmail, setTenantEmail] = useState('');
    const [propertyId, setPropertyId] = useState('');
    const [leaseStart, setLeaseStart] = useState('');
    const [leaseEnd, setLeaseEnd] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const fetchTenantsData = async () => {
        if (!user.id) return;
        try {
            setLoading(true);

            const { data: props } = await supabase
                .from('properties')
                .select('id, name')
                .eq('owner_id', user.id);

            setProperties(props || []);
            const propertyIds = (props || []).map(p => p.id);

            if (propertyIds.length === 0) {
                setTenantLinks([]);
                setLoading(false);
                return;
            }

            const { data: links, error } = await supabase
                .from('tenant_links')
                .select('*')
                .in('property_id', propertyIds)
                .order('created_at', { ascending: false });

            if (error) {
                console.error('Error fetching tenant links:', error);
            } else {
                setTenantLinks(links || []);
            }

        } catch (err) {
            console.error('Unexpected error in TenantsPage:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTenantsData();
    }, [user.id]);

    const tenantLimit = getPlanLimits(user.plan).tenants;

    const handleAddClick = () => {
        if (!editingTenant && tenantLinks.length >= tenantLimit) {
            setShowUpgradeGate(true);
            return;
        }
        setEditingTenant(null);
        setTenantName(''); setTenantEmail(''); setPropertyId(''); setLeaseStart(''); setLeaseEnd('');
        setIsAddModalOpen(true);
    };

    const handleSaveTenant = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!tenantName || !tenantEmail || !propertyId) return;

        // Re-check limit at submit time (race condition guard)
        if (!editingTenant && tenantLinks.length >= tenantLimit) {
            setIsAddModalOpen(false);
            setShowUpgradeGate(true);
            return;
        }

        setIsSubmitting(true);

        try {
            if (editingTenant) {
                const { error } = await supabase
                    .from('tenant_links')
                    .update({
                        tenant_name: tenantName,
                        tenant_email: tenantEmail,
                        property_id: propertyId,
                        lease_start: leaseStart || null,
                        lease_end: leaseEnd || null,
                    })
                    .eq('id', editingTenant.id);

                if (error) throw error;
            } else {
                const inviteCode = Math.random().toString(36).substring(2, 8).toUpperCase();

                const { error } = await supabase
                    .from('tenant_links')
                    .insert({
                        tenant_name: tenantName,
                        tenant_email: tenantEmail,
                        property_id: propertyId,
                        lease_start: leaseStart || null,
                        lease_end: leaseEnd || null,
                        invite_code: inviteCode,
                        invite_status: 'invited',
                        created_at: new Date().toISOString()
                    });

                if (error) throw error;
            }

            setIsAddModalOpen(false);
            setEditingTenant(null);
            setTenantName(''); setTenantEmail(''); setPropertyId(''); setLeaseStart(''); setLeaseEnd('');
            fetchTenantsData();
        } catch (err: any) {
            console.error('Error saving tenant:', err);
            alert('Error saving tenant: ' + err.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    const openEditModal = (tenant: TenantLink) => {
        setEditingTenant(tenant);
        setTenantName(tenant.tenant_name);
        setTenantEmail(tenant.tenant_email);
        setPropertyId(tenant.property_id);
        setLeaseStart(tenant.lease_start || '');
        setLeaseEnd(tenant.lease_end || '');
        setIsAddModalOpen(true);
        setOpenMenuTenantId(null);
        setMenuPos(null);
    };

    const copyInviteCode = (code: string, id: string) => {
        navigator.clipboard.writeText(code);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 2000);
    };

    const handleDeleteTenant = async (id: string) => {
        if (!confirm('Are you sure you want to remove this tenant? This will unlink them from the property.')) return;

        try {
            const { error } = await supabase
                .from('tenant_links')
                .delete()
                .eq('id', id);

            if (error) {
                alert('Error deleting tenant: ' + error.message);
            } else {
                fetchTenantsData();
            }
        } catch (err) {
            console.error('Delete error:', err);
        }
    };

    const handleOpenMenu = (e: React.MouseEvent<HTMLButtonElement>, tenantId: string) => {
        e.stopPropagation();
        if (openMenuTenantId === tenantId) {
            setOpenMenuTenantId(null);
            setMenuPos(null);
            return;
        }
        const rect = e.currentTarget.getBoundingClientRect();
        const MENU_HEIGHT = 160;
        const MENU_WIDTH = 220;
        const spaceBelow = window.innerHeight - rect.bottom;
        const openUpward = spaceBelow < MENU_HEIGHT && rect.top > MENU_HEIGHT;
        setMenuPos({
            top: openUpward ? rect.top : rect.bottom + 6,
            left: Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8),
            openUpward,
        });
        setOpenMenuTenantId(tenantId);
    };

    const filteredTenants = tenantLinks.filter(t =>
        t.tenant_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        t.tenant_email.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const activeLink = filteredTenants.find(l => l.id === openMenuTenantId) ?? null;

    // Live presence (online / last seen) for every tenant linked to this landlord.
    const presence = usePresenceFor(tenantLinks.map(t => t.tenant_user_id));

    return (
        <div className="page dashboard-page">
            <div className="page__header dashboard-page-header">
                <div className="dashboard-page-heading">
                    <h1 className="page__title" style={{ fontSize: '2.5rem', fontWeight: 900, letterSpacing: '-0.04em', marginBottom: '8px', color: '#1a1d2e' }}>Tenants</h1>
                    <p className="page__subtitle" style={{ fontSize: '1.125rem', fontWeight: 500, color: 'var(--color-text-secondary)' }}>Manage your residents and lease agreements</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    {!loading && (
                        <span style={{ fontSize: '0.8125rem', fontWeight: 700, color: tenantLinks.length >= tenantLimit ? '#ef4444' : '#64748b', background: tenantLinks.length >= tenantLimit ? 'rgba(239,68,68,0.08)' : '#f1f5f9', padding: '6px 12px', borderRadius: '10px' }}>
                            {tenantLinks.length} / {formatLimit(tenantLimit)} tenants
                        </span>
                    )}
                    <button
                        className="btn btn-primary"
                        onClick={handleAddClick}
                        disabled={loading}
                        style={{
                            display: 'flex', alignItems: 'center', gap: '10px', borderRadius: '14px',
                            padding: '0.75rem 1.75rem', fontWeight: 700,
                            background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                            border: 'none', boxShadow: '0 10px 15px -3px rgba(79,70,229,0.3)'
                        }}
                    >
                        <Plus size={20} /> Add Tenant
                    </button>
                </div>
            </div>

            {/* Search bar */}
            {!loading && tenantLinks.length > 0 && (
                <div className="dashboard-filter-bar" style={{
                    marginBottom: '2.5rem', background: '#fff', padding: '1.25rem',
                    borderRadius: '16px', border: '1px solid #f1f5f9',
                    boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02)'
                }}>
                    <div style={{ position: 'relative', flex: 1 }}>
                        <Search size={18} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: 'var(--color-text-tertiary)' }} />
                        <input
                            type="text"
                            placeholder="Search tenants by name or email..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            style={{
                                width: '100%', padding: '0.625rem 1rem 0.625rem 2.5rem',
                                border: '1px solid var(--color-border)', borderRadius: '8px', fontSize: '0.875rem'
                            }}
                        />
                    </div>
                </div>
            )}

            {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '5rem' }}>
                    <Loader2 className="animate-spin" size={40} color="var(--color-primary)" />
                </div>
            ) : tenantLinks.length === 0 ? (
                <EmptyState
                    icon={<Users />}
                    title="No tenants found"
                    description="You haven't added any tenants yet. Link a tenant to one of your properties to start tracking rent and maintenance."
                    actionLabel="Add Tenant"
                    onAction={handleAddClick}
                />
            ) : (
                <div className="data-panel responsive-table" style={{ border: 'none', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.02), 0 10px 15px -3px rgba(0,0,0,0.03)', borderRadius: '24px', background: '#fff' }}>
                    <table className="data-table" style={{ background: '#fff', borderCollapse: 'separate', borderSpacing: 0, minWidth: '920px' }}>
                        <thead style={{ background: 'var(--color-bg-subtle)' }}>
                            <tr>
                                <th style={{ padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)' }}>Tenant</th>
                                <th style={{ padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)' }}>Property</th>
                                <th style={{ padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)' }}>Lease Period</th>
                                <th style={{ padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)' }}>Invite Code</th>
                                <th style={{ padding: '1.5rem', fontSize: '0.8125rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-tertiary)', borderBottom: '1px solid var(--color-border-subtle)' }}>Status</th>
                                <th style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredTenants.map((link) => {
                                const property = properties.find(p => p.id === link.property_id);
                                return (
                                    <tr key={link.id} style={{ transition: 'background 0.2s' }} className="data-table__row">
                                        <td style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '1.25rem' }}>
                                                <div style={{ position: 'relative', flexShrink: 0 }}>
                                                    <div className="avatar avatar--blue" style={{ width: '48px', height: '48px', fontSize: '1rem', fontWeight: 700, borderRadius: '14px' }}>
                                                        {link.tenant_name.split(' ').map((n: string) => n[0]).join('').substring(0, 2).toUpperCase()}
                                                    </div>
                                                    {link.tenant_user_id && (
                                                        <PresenceIndicator
                                                            lastSeen={presence.get(link.tenant_user_id)}
                                                            dotPlacement="overlay"
                                                            size={11}
                                                        />
                                                    )}
                                                </div>
                                                <div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                        <span style={{ fontWeight: 800, color: 'var(--color-text)', fontSize: '1rem' }}>{link.tenant_name}</span>
                                                        {link.tenant_user_id && (
                                                            <PresenceIndicator
                                                                lastSeen={presence.get(link.tenant_user_id)}
                                                                variant="full"
                                                            />
                                                        )}
                                                    </div>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: 'var(--color-text-tertiary)', fontSize: '0.875rem' }}>
                                                        <Mail size={13} /> {link.tenant_email}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-text-secondary)' }}>
                                                <div style={{ width: 32, height: 32, borderRadius: '8px', background: 'var(--color-bg-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                    <Building2 size={16} color="var(--color-primary)" />
                                                </div>
                                                <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>{property?.name || 'Unassigned'}</span>
                                            </div>
                                        </td>
                                        <td style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--color-text-secondary)', fontSize: '0.9375rem', fontWeight: 700 }}>
                                                    <Calendar size={15} opacity={0.6} />
                                                    {link.lease_start ? formatDate(link.lease_start) : 'Not set'}
                                                </div>
                                                {link.lease_end && (
                                                    <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-tertiary)', paddingLeft: '23px', fontWeight: 500 }}>
                                                        until {formatDate(link.lease_end)}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                                <code style={{ background: '#f1f5f9', padding: '6px 12px', borderRadius: '8px', fontSize: '0.875rem', fontWeight: 800, color: 'var(--color-primary)', letterSpacing: '0.02em' }}>
                                                    {link.invite_code}
                                                </code>
                                                <button
                                                    onClick={() => copyInviteCode(link.invite_code, link.id)}
                                                    style={{ background: '#fff', border: '1px solid var(--color-border)', cursor: 'pointer', color: copiedId === link.id ? 'var(--color-green)' : 'var(--color-text-tertiary)', padding: '6px', borderRadius: '8px', display: 'flex', transition: 'all 0.2s' }}
                                                    className="copy-btn-hover"
                                                    title="Copy Invite Code"
                                                >
                                                    {copiedId === link.id ? <Check size={16} /> : <Clipboard size={16} />}
                                                </button>
                                            </div>
                                        </td>
                                        <td style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)' }}>
                                            <span style={{
                                                padding: '6px 12px', fontSize: '0.75rem', fontWeight: 800, borderRadius: '10px',
                                                display: 'inline-flex', alignItems: 'center', gap: '6px',
                                                background: link.invite_status === 'active' ? 'rgba(34,197,94,0.1)' : 'rgba(234,179,8,0.1)',
                                                color: link.invite_status === 'active' ? '#16a34a' : '#ca8a04',
                                                textTransform: 'uppercase', letterSpacing: '0.02em'
                                            }}>
                                                <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'currentColor' }} />
                                                {link.invite_status === 'active' ? 'Active' : 'Pending'}
                                            </span>
                                        </td>
                                        <td style={{ padding: '1.5rem', borderBottom: '1px solid var(--color-border-subtle)', textAlign: 'right' }}>
                                            <button
                                                onClick={(e) => handleOpenMenu(e, link.id)}
                                                style={{
                                                    background: openMenuTenantId === link.id ? '#f1f5f9' : 'none',
                                                    border: 'none', cursor: 'pointer',
                                                    color: 'var(--color-text-tertiary)',
                                                    padding: '8px', borderRadius: '50%', display: 'flex',
                                                    transition: 'background 0.15s',
                                                }}
                                                onMouseEnter={e => { if (openMenuTenantId !== link.id) e.currentTarget.style.background = '#f8fafc'; }}
                                                onMouseLeave={e => { if (openMenuTenantId !== link.id) e.currentTarget.style.background = 'none'; }}
                                            >
                                                <MoreHorizontal size={20} />
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Portal dropdown — rendered at document.body, immune to parent overflow */}
            {openMenuTenantId && menuPos && activeLink && (
                <TenantMenu
                    key={openMenuTenantId}
                    pos={menuPos}
                    onEdit={() => openEditModal(activeLink)}
                    onDelete={() => handleDeleteTenant(activeLink.id)}
                    onClose={() => { setOpenMenuTenantId(null); setMenuPos(null); }}
                />
            )}

            {/* Add / Edit Tenant Modal */}
            {isAddModalOpen && (
                <div
                    className="dropdown-overlay dashboard-modal-shell"
                    onClick={() => { setIsAddModalOpen(false); setEditingTenant(null); }}
                    style={{ background: 'rgba(15,23,42,0.65)' }}
                >
                    <div
                        className="dashboard-modal"
                        style={{
                            background: '#fff', padding: '3.5rem',
                            boxShadow: '0 30px 60px -12px rgba(0,0,0,0.35)',
                            animation: 'slideUp 0.4s cubic-bezier(0.16,1,0.3,1)'
                        }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '3rem' }}>
                            <div>
                                <div style={{ display: 'inline-flex', padding: '10px', borderRadius: '14px', background: 'rgba(99,102,241,0.08)', color: '#16a34a', marginBottom: '1rem' }}>
                                    <Users size={24} />
                                </div>
                                <h3 style={{ margin: 0, fontSize: '2rem', fontWeight: 900, letterSpacing: '-0.04em', color: '#1a1d2e' }}>
                                    {editingTenant ? 'Edit Tenant' : 'Add Tenant'}
                                </h3>
                                <p style={{ fontSize: '1rem', color: 'var(--color-text-secondary)', marginTop: '8px', fontWeight: 500 }}>
                                    {editingTenant ? 'Update resident details and lease period' : 'Link a new resident to your property'}
                                </p>
                            </div>
                            <button
                                onClick={() => { setIsAddModalOpen(false); setEditingTenant(null); }}
                                style={{ background: '#f8fafc', border: '1px solid #e2e8f0', cursor: 'pointer', color: '#64748b', padding: '10px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            >
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSaveTenant} style={{ gap: '2.5rem', display: 'flex', flexDirection: 'column' }}>
                            <div className="auth-field">
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Full Name</label>
                                <input
                                    type="text"
                                    placeholder="e.g. John Smith"
                                    value={tenantName}
                                    onChange={e => setTenantName(e.target.value)}
                                    required
                                    style={{ borderRadius: '16px', padding: '1.25rem', border: '1px solid #e2e8f0', fontSize: '1.0625rem', width: '100%', background: '#f8fafc', fontWeight: 600, color: '#1a1d2e', outline: 'none' }}
                                />
                            </div>

                            <div className="auth-field">
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email Address</label>
                                <input
                                    type="email"
                                    placeholder="john.smith@example.com"
                                    value={tenantEmail}
                                    onChange={e => setTenantEmail(e.target.value)}
                                    required
                                    style={{ borderRadius: '16px', padding: '1.25rem', border: '1px solid #e2e8f0', fontSize: '1.0625rem', width: '100%', background: '#f8fafc', fontWeight: 600, color: '#1a1d2e', outline: 'none' }}
                                />
                            </div>

                            <div className="auth-field">
                                <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assign to Property</label>
                                <select
                                    value={propertyId}
                                    onChange={e => setPropertyId(e.target.value)}
                                    required
                                    style={{ width: '100%', padding: '1.25rem', borderRadius: '16px', border: '1px solid #e2e8f0', fontSize: '1.0625rem', background: '#f8fafc', fontWeight: 600, cursor: 'pointer', appearance: 'none' }}
                                >
                                    <option value="">Select a property</option>
                                    {properties.map(p => (
                                        <option key={p.id} value={p.id}>{p.name}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="dashboard-form-grid dashboard-form-grid--equal">
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lease Start</label>
                                    <input
                                        type="date"
                                        value={leaseStart}
                                        onChange={e => setLeaseStart(e.target.value)}
                                        style={{ borderRadius: '16px', padding: '1.25rem', border: '1px solid #e2e8f0', fontSize: '1.0625rem', width: '100%', background: '#f8fafc', fontWeight: 600, color: '#1a1d2e', outline: 'none' }}
                                    />
                                </div>
                                <div className="auth-field">
                                    <label style={{ fontSize: '0.8125rem', fontWeight: 800, marginBottom: '10px', display: 'block', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Lease End</label>
                                    <input
                                        type="date"
                                        value={leaseEnd}
                                        onChange={e => setLeaseEnd(e.target.value)}
                                        style={{ borderRadius: '16px', padding: '1.25rem', border: '1px solid #e2e8f0', fontSize: '1.0625rem', width: '100%', background: '#f8fafc', fontWeight: 600, color: '#1a1d2e', outline: 'none' }}
                                    />
                                </div>
                            </div>

                            <div className="dashboard-modal__actions" style={{ paddingTop: '2.5rem' }}>
                                <button type="button" className="btn btn-secondary" onClick={() => { setIsAddModalOpen(false); setEditingTenant(null); }} style={{ padding: '1.125rem 2.25rem', borderRadius: '16px', fontWeight: 700, fontSize: '1rem' }}>Cancel</button>
                                <button type="submit" className="btn btn-primary" disabled={isSubmitting || !tenantName || !tenantEmail || !propertyId} style={{ padding: '1.125rem 3.5rem', borderRadius: '16px', fontWeight: 800, fontSize: '1rem', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', boxShadow: '0 12px 24px -6px rgba(22,163,74,0.30)' }}>
                                    {isSubmitting ? (editingTenant ? 'Saving...' : 'Adding...') : (editingTenant ? 'Save Changes' : 'Add Tenant')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Upgrade Gate Modal */}
            {showUpgradeGate && (
                <div
                    className="dropdown-overlay dashboard-modal-shell"
                    onClick={() => setShowUpgradeGate(false)}
                    style={{ background: 'rgba(15,23,42,0.65)', animation: 'fadeIn 0.2s ease-out', backdropFilter: 'blur(4px)' }}
                >
                    <div
                        style={{ background: '#fff', borderRadius: '28px', padding: '0', maxWidth: '460px', width: '90%', boxShadow: '0 30px 60px -12px rgba(0,0,0,0.35)', animation: 'slideUp 0.35s cubic-bezier(0.16,1,0.3,1)', overflow: 'hidden' }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div style={{ background: 'linear-gradient(135deg, #15803d 0%, #7c3aed 100%)', padding: '2.5rem 2.5rem 3.5rem', position: 'relative', overflow: 'hidden' }}>
                            <button onClick={() => setShowUpgradeGate(false)} style={{ position: 'absolute', top: '1.25rem', right: '1.25rem', background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer', color: '#fff', padding: '6px', borderRadius: '50%', display: 'flex' }}>
                                <X size={16} />
                            </button>
                            <div style={{ width: '52px', height: '52px', background: 'rgba(255,255,255,0.15)', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem' }}>
                                <Lock size={24} color="#fff" />
                            </div>
                            <h3 style={{ margin: '0 0 8px', fontSize: '1.625rem', fontWeight: 900, color: '#fff', letterSpacing: '-0.03em' }}>Unlock More Tenants</h3>
                            <p style={{ margin: 0, fontSize: '0.9375rem', color: 'rgba(255,255,255,0.75)', fontWeight: 500, lineHeight: 1.5 }}>
                                You're on the Free plan — limited to {tenantLimit} tenants. Upgrade to manage unlimited tenants on Pro.
                            </p>
                            <Users size={120} style={{ position: 'absolute', bottom: '-20px', right: '-10px', opacity: 0.1, transform: 'rotate(-10deg)' }} />
                        </div>
                        <div style={{ padding: '2rem 2.5rem', borderBottom: '1px solid #f1f5f9' }}>
                            <p style={{ margin: '0 0 1rem', fontSize: '0.75rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#94a3b8' }}>What you get with Pro — €15/mo</p>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                {['Unlimited tenants across your properties', 'Unlimited properties in your portfolio', 'Document e-signing for lease agreements'].map(f => (
                                    <div key={f} style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '0.9375rem', fontWeight: 600, color: '#1e293b' }}>
                                        <CheckCircle2 size={18} color="#16a34a" style={{ flexShrink: 0 }} /> {f}
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div style={{ padding: '1.75rem 2.5rem', display: 'flex', gap: '12px' }}>
                            <button onClick={() => setShowUpgradeGate(false)} style={{ flex: 1, padding: '0.875rem', borderRadius: '14px', border: '1px solid #e2e8f0', background: '#f8fafc', fontWeight: 600, fontSize: '0.9375rem', color: '#64748b', cursor: 'pointer' }}>Maybe later</button>
                            <button onClick={() => { setShowUpgradeGate(false); navigate('/app/landlord/settings'); }} style={{ flex: 2, padding: '0.875rem', borderRadius: '14px', border: 'none', background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)', fontWeight: 800, fontSize: '0.9375rem', color: '#fff', cursor: 'pointer', boxShadow: '0 8px 20px -6px rgba(22,163,74,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                <Zap size={16} /> Upgrade to Pro
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
                @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
                @keyframes tenantMenuIn { from { opacity: 0; transform: scale(0.96) translateY(-4px); } to { opacity: 1; transform: scale(1) translateY(0); } }
                .copy-btn-hover:hover { border-color: var(--color-primary) !important; color: var(--color-primary) !important; }
            `}</style>
        </div>
    );
}
